'use client';

import React, { useMemo, useState } from 'react';
import { PlusIcon, TrashIcon } from '@heroicons/react/24/outline';
import { normalize } from '@/app/services/degreeRequirementService';
import { usePlanner } from '../plannerContext';
import { programColor, requirementsForCourse, type PlannerTerm } from '../plannerModel';
import { termOrderKey, type ProposalTerm } from '../planProposal';
import { CourseCard, CreditMeter, GhostCard, PlaceholderCard, SlotRow, useIsMobile } from '../PlannerPrimitives';

type DragPayload = { type: 'item'; semesterNumber: number; index: number } | { type: 'slot'; key: string };
const DRAG_MIME = 'application/x-scarlet-planner';

function readDrag(event: React.DragEvent): DragPayload | null {
  try { return JSON.parse(event.dataTransfer.getData(DRAG_MIME)) as DragPayload; } catch { return null; }
}

// ─── Keyboard path ──────────────────────────────────────────────────────────────────────
// Every card sits in a [data-board-card] wrapper inside a [data-board-column]. Arrow keys move
// focus between cards, M opens "Move to" for a planned item, and Enter (the card is a button)
// opens the peek. Dragging is never the only way to move something.

const cardButton = (card: Element | undefined) => card?.querySelector<HTMLElement>(':scope > button') ?? null;

function handleBoardKeyDown(event: React.KeyboardEvent<HTMLElement>, openMove: (semesterNumber: number, index: number, anchor: HTMLElement) => void) {
  if (event.altKey || event.ctrlKey || event.metaKey || event.defaultPrevented) return;
  const card = (event.target as HTMLElement).closest<HTMLElement>('[data-board-card]');
  if (!card || cardButton(card) !== event.target) return;
  const columns = Array.from(event.currentTarget.querySelectorAll<HTMLElement>('[data-board-column]'));
  const cardsIn = (column: HTMLElement) => Array.from(column.querySelectorAll<HTMLElement>('[data-board-card]'));
  const col = columns.findIndex((column) => column.contains(card));
  if (col < 0) return;
  const row = cardsIn(columns[col]).indexOf(card);
  let next: HTMLElement | undefined;
  if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
    next = cardsIn(columns[col])[row + (event.key === 'ArrowDown' ? 1 : -1)];
  } else if (event.key === 'ArrowRight' || event.key === 'ArrowLeft') {
    const step = event.key === 'ArrowRight' ? 1 : -1;
    for (let c = col + step; c >= 0 && c < columns.length; c += step) {
      const cards = cardsIn(columns[c]);
      if (cards.length) { next = cards[Math.min(row, cards.length - 1)]; break; }
    }
  } else if ((event.key === 'm' || event.key === 'M') && card.dataset.moveSemester) {
    event.preventDefault();
    openMove(Number(card.dataset.moveSemester), Number(card.dataset.moveIndex), event.target as HTMLElement);
    return;
  } else {
    return;
  }
  event.preventDefault();
  const button = cardButton(next);
  if (button) { button.focus(); button.scrollIntoView({ block: 'nearest', inline: 'nearest' }); }
}

function SuggestedBlock({ ghost, newTerm = false }: { ghost: ProposalTerm; newTerm?: boolean }) {
  const { programs, nameOf, creditsOf, listLabel, actions, proposalBusy } = usePlanner();
  return (
    <div className="flex flex-col gap-2 rounded-xl border border-dashed border-[color-mix(in_srgb,var(--ss-brand)_55%,transparent)] bg-[var(--ss-brand-bg)] p-2" role="group" aria-label={`Suggested for ${ghost.name}`}>
      <div className="flex items-center justify-between gap-2 px-0.5">
        <span className="text-[11px] font-bold uppercase tracking-[0.06em] text-[var(--ss-brand)]">Suggested · +{ghost.credits} cr</span>
        <button type="button" disabled={proposalBusy} onClick={() => actions.acceptProposal([ghost.name])}
          className="min-h-12 rounded-lg border border-[var(--ss-brand)] bg-[var(--ss-surface)] px-3 md:min-h-8 md:px-2.5 text-xs font-semibold text-[var(--ss-brand)] hover:bg-[var(--ss-brand)] hover:text-[var(--ss-brand-contrast)] disabled:opacity-50">
          {newTerm ? 'Add term' : 'Accept term'}<span className="sr-only">: {ghost.name}</span>
        </button>
      </div>
      {ghost.items.map((item, i) => {
        if (item.kind === 'course_list') {
          const programIndex = programs.find((p) => p.requirements.some((r) => r.listNames.includes(item.mappingName)))?.programIndex ?? null;
          return <GhostCard key={i} title={item.label || listLabel(item.mappingName)} detail={`about ${item.credits || 3} cr · placeholder`} programIndex={programIndex} />;
        }
        const code = normalize(item.code);
        const programIndex = requirementsForCourse(code, programs)[0]?.programIndex ?? null;
        return <GhostCard key={i} title={nameOf(code)} code={code} detail={`${Number(item.credits) || creditsOf(code) || '?'} cr`} programIndex={programIndex} />;
      })}
    </div>
  );
}

/** Codes related to the selected course: what it needs and what it opens up. */
export function useRelations() {
  const { selectedCode, index } = usePlanner();
  return useMemo(() => {
    if (!selectedCode) return { prereqs: new Set<string>(), unlocks: new Set<string>() };
    const prereqs = new Set((index.prereqGroups.get(selectedCode) || []).flat(2).filter((c) => !c.startsWith('>=')).map(normalize));
    const unlocks = new Set(index.dependents.get(selectedCode) || []);
    return { prereqs, unlocks };
  }, [selectedCode, index]);
}

export function RequirementRail({ className = '' }: { className?: string }) {
  const { programs, focusRequirementKey, setFocusRequirementKey, open, actions } = usePlanner();
  const [openOnly, setOpenOnly] = useState(true);
  return (
    <aside className={`flex flex-col gap-1 ${className}`} aria-label="What's left" data-release-scroll-reset>
      <div className="flex items-center justify-between px-4 pb-1">
        <h2 className="text-[13px] font-bold">What&apos;s left</h2>
        <div className="inline-flex rounded-lg bg-[var(--ss-surface-subtle)] p-0.5 ring-1 ring-inset ring-[var(--ss-border)]" role="group" aria-label="Requirement filter">
          {[true, false].map((value) => (
            <button key={String(value)} type="button" aria-pressed={openOnly === value} onClick={() => setOpenOnly(value)}
              className={`min-h-12 rounded-md px-2.5 py-0.5 text-xs font-semibold lg:min-h-0 ${openOnly === value ? 'bg-[var(--ss-surface)] text-[var(--ss-text)] shadow-e1' : 'text-[var(--ss-text-muted)]'}`}>{value ? 'Open' : 'All'}</button>
          ))}
        </div>
      </div>
      {!programs.length ? (
        <div className="mx-4 rounded-xl border border-dashed border-[var(--ss-border)] p-4 text-sm text-[var(--ss-text-muted)]">
          Add a major, minor or certificate to see what&apos;s left.
          <button type="button" onClick={actions.importDegree} className="mt-2 block font-semibold text-[var(--ss-brand)]">Add a program</button>
        </div>
      ) : null}
      {programs.map((program) => {
        const hidden = program.requirements.filter((r) => r.openCount === 0 && focusRequirementKey !== r.key).length;
        return (
          <section key={program.degreeId} className="flex flex-col pb-3">
            <div className="px-4 pb-1 pt-2">
              <p className="flex items-center gap-2 text-[13.5px] font-bold"><span className="h-4 w-1 rounded-full" style={{ backgroundColor: programColor(program.programIndex) }} aria-hidden="true" />{program.degreeName}</p>
              <p className="pl-3 text-xs text-[var(--ss-text-muted)]">
                {program.captured ? `${program.slotTotals.earned + program.slotTotals.planned} of ${program.slotTotals.needed} covered · ${program.slotTotals.open} open` : 'Requirements not captured for this program'}
              </p>
            </div>
            {program.requirements.map((req) => {
              if (openOnly && req.openCount === 0 && focusRequirementKey !== req.key) return null;
              const focused = focusRequirementKey === req.key;
              return (
                <div key={req.key} className={`grid grid-cols-[1fr_auto] gap-x-2 gap-y-1 border-l-[3px] py-2 pl-6 pr-4 ${focused ? 'border-[var(--ss-brand)] bg-[var(--ss-surface-subtle)]' : 'border-transparent hover:bg-[var(--ss-surface-subtle)]'}`}>
                  <button type="button" aria-pressed={focused} onClick={() => setFocusRequirementKey(focused ? null : req.key)} className={`min-h-12 text-left text-[13px] leading-snug lg:min-h-0 ${req.openCount ? 'font-semibold' : 'text-[var(--ss-text-muted)]'}`}>
                    {req.title}
                    <span className="sr-only">{focused ? ', highlighted on the board' : ', highlight on the board'}</span>
                  </button>
                  <span className="text-xs tabular-nums text-[var(--ss-text-muted)]">
                    {req.mode === 'courses' ? `${req.slots.length - req.openCount} of ${req.slots.length}` : `${req.creditsEarned + req.creditsPlanned}/${req.creditsNeeded} cr`}
                  </span>
                  <span className="col-span-2">{req.mode === 'courses' ? <SlotRow slots={req.slots} /> : <CreditMeter earned={req.creditsEarned} planned={req.creditsPlanned} needed={req.creditsNeeded} />}</span>
                  {req.openCount ? (
                    <span className="col-span-2 mt-0.5 flex flex-wrap gap-1.5">
                      <button
                        type="button"
                        draggable
                        onDragStart={(e) => { e.dataTransfer.setData(DRAG_MIME, JSON.stringify({ type: 'slot', key: req.key })); e.dataTransfer.effectAllowed = 'copy'; }}
                        onClick={(e) => open({ kind: 'slot', requirementKey: req.key, anchor: e.currentTarget })}
                        className="min-h-12 cursor-grab rounded-lg border-[1.5px] border-dashed border-[var(--ss-text-muted)]/50 bg-[var(--ss-surface)] px-2 py-0.5 text-xs lg:min-h-0 font-semibold hover:border-[var(--ss-brand)] hover:text-[var(--ss-brand)]"
                      >
                        + Fill {req.openCount > 1 ? `${req.openCount} slots` : 'slot'}
                      </button>
                    </span>
                  ) : null}
                </div>
              );
            })}
            {openOnly && hidden ? (
              <button type="button" onClick={() => setOpenOnly(false)} className="min-h-12 px-6 pt-1 text-left text-xs text-[var(--ss-text-muted)] hover:text-[var(--ss-text)] lg:min-h-0">{hidden} complete or fully planned · show</button>
            ) : null}
          </section>
        );
      })}
      {programs.length ? <p className="px-4 pt-2 text-[11px] text-[var(--ss-text-muted)]">Requirements as captured in Scarlet Sync. Not an official audit; confirm grade minimums and residency with an advisor.</p> : null}
    </aside>
  );
}

function LoadMeter({ credits }: { credits: number }) {
  const label = credits === 0 ? 'Empty' : credits < 12 ? `${credits} cr · below full time` : credits > 18 ? `${credits} cr · heavy` : `${credits} cr`;
  const warn = credits > 0 && (credits < 12 || credits > 18);
  return (
    <div className="flex flex-col gap-1">
      <div className="relative h-1.5 rounded-full bg-[var(--ss-surface-subtle)]" title="12–18 credits is a full-time load" aria-hidden="true">
        <span className="absolute -bottom-0.5 -top-0.5 border-x border-dashed border-[var(--ss-border)]" style={{ left: `${(12 / 21) * 100}%`, width: `${(6 / 21) * 100}%` }} />
        <span className="absolute inset-y-0 left-0 rounded-full bg-[var(--ss-text)] opacity-70 transition-[width] duration-200 motion-reduce:transition-none" style={{ width: `${(Math.min(credits, 21) / 21) * 100}%` }} />
      </div>
      <span className={`text-[11.5px] ${warn ? 'font-semibold text-[var(--ss-warning)]' : 'text-[var(--ss-text-muted)]'}`}>{label}</span>
    </div>
  );
}

function TermColumn({ term, relations, ghost }: { term: PlannerTerm; relations: ReturnType<typeof useRelations>; ghost?: ProposalTerm }) {
  const planner = usePlanner();
  const { programs, issuesByCode, nameOf, creditsOf, selectedCode, focusRequirementKey, requirementByKey, open, actions, listLabel, listCredits } = planner;
  const [over, setOver] = useState(false);
  const focusPool = focusRequirementKey ? new Set(requirementByKey.get(focusRequirementKey)?.pool || []) : null;
  const focusLists = focusRequirementKey ? new Set(requirementByKey.get(focusRequirementKey)?.listNames || []) : null;

  return (
    <section
      aria-label={`${term.name}, ${term.credits} credits`}
      data-board-column={term.number}
      onDragOver={(e) => { if (e.dataTransfer.types.includes(DRAG_MIME)) { e.preventDefault(); setOver(true); } }}
      onDragLeave={(e) => { if (!e.currentTarget.contains(e.relatedTarget as Node)) setOver(false); }}
      onDrop={(e) => {
        e.preventDefault();
        setOver(false);
        const payload = readDrag(e);
        if (!payload) return;
        if (payload.type === 'item') { if (payload.semesterNumber !== term.number) actions.moveItem(payload.semesterNumber, payload.index, term.number); }
        else open({ kind: 'slot', requirementKey: payload.key, semesterNumber: term.number, anchor: e.currentTarget as HTMLElement });
      }}
      className={`flex min-h-[220px] flex-col rounded-2xl border transition-colors duration-150 motion-reduce:transition-none md:min-h-[420px] ${over ? 'border-[var(--ss-brand)] bg-[var(--ss-brand-bg)]' : 'border-[var(--ss-border)] bg-[var(--ss-surface)]'}`}
    >
      <header className="flex flex-col gap-2 border-b border-[var(--ss-border)] p-3">
        <div className="flex items-baseline justify-between gap-2">
          <h3 className="text-sm font-bold">{term.name}</h3>
          <span className="flex items-center gap-1 text-xs tabular-nums text-[var(--ss-text-muted)]">
            {term.items.length} item{term.items.length === 1 ? '' : 's'}
            <button type="button" onClick={() => actions.removeTerm(term.number)} aria-label={`Remove ${term.name}`} title={`Remove ${term.name}`}
              className="flex h-12 w-12 items-center justify-center rounded text-[var(--ss-text-muted)] opacity-60 md:h-auto md:w-auto md:p-0.5 hover:bg-[var(--ss-critical-bg)] hover:text-[var(--ss-critical)] hover:opacity-100 focus-visible:opacity-100">
              <TrashIcon className="h-3.5 w-3.5" aria-hidden="true" />
            </button>
          </span>
        </div>
        <LoadMeter credits={term.credits} />
      </header>
      <div className="flex flex-1 flex-col gap-2 p-2.5">
        {term.items.map(({ item, index }) => {
          const drag = {
            draggable: true,
            onDragStart: (e: React.DragEvent<HTMLButtonElement>) => { e.dataTransfer.setData(DRAG_MIME, JSON.stringify({ type: 'item', semesterNumber: term.number, index })); e.dataTransfer.effectAllowed = 'move'; planner.close(); },
          };
          const boardCard = { 'data-board-card': '', 'data-move-semester': term.number, 'data-move-index': index };
          if (item.kind === 'course_list') {
            const programIndex = programs.find((p) => p.requirements.some((r) => r.listNames.includes(item.mappingName)))?.programIndex ?? null;
            if (item.selectedCourseCode) {
              const code = normalize(item.selectedCourseCode);
              return (
                <div key={`${term.number}-${index}`} data-plan-code={code} {...boardCard}>
                  <CourseCard code={code} title={nameOf(code)} credits={creditsOf(code)} programIndexes={programIndex === null ? [] : [programIndex]}
                    hasIssue={issuesByCode.has(code)} selected={selectedCode === code} dimmed={Boolean(focusLists && !focusLists.has(item.mappingName) && !focusPool?.has(code))}
                    onOpen={(el) => open({ kind: 'list', semesterNumber: term.number, index, anchor: el })} draggableProps={drag} />
                </div>
              );
            }
            return (
              <div key={`${term.number}-${index}`} {...boardCard}>
                <PlaceholderCard label={item.label || listLabel(item.mappingName)} credits={item.credits || listCredits(item.mappingName)} programIndex={programIndex}
                  dimmed={Boolean(focusLists && !focusLists.has(item.mappingName))}
                  onOpen={(el) => open({ kind: 'list', semesterNumber: term.number, index, anchor: el })} draggableProps={drag} />
              </div>
            );
          }
          const code = normalize(item.code);
          const programIndexes = requirementsForCourse(code, programs).map((r) => r.programIndex);
          return (
            <div key={`${term.number}-${index}`} data-plan-code={code} {...boardCard}>
              <CourseCard
                code={code}
                title={nameOf(code)}
                credits={Number(item.credits) || creditsOf(code)}
                programIndexes={programIndexes}
                hasIssue={issuesByCode.has(code)}
                selected={selectedCode === code}
                relation={relations.prereqs.has(code) ? 'prerequisite' : relations.unlocks.has(code) ? 'unlocks' : null}
                dimmed={Boolean(focusPool && !focusPool.has(code))}
                onOpen={(el) => open({ kind: 'course', code, anchor: el })}
                draggableProps={drag}
              />
            </div>
          );
        })}
        {ghost ? <SuggestedBlock ghost={ghost} /> : null}
        <button type="button" onClick={(e) => open({ kind: 'term-add', semesterNumber: term.number, anchor: e.currentTarget })} aria-label={`Add to ${term.name}`} className="flex min-h-12 items-center md:min-h-10 justify-center gap-1.5 rounded-[10px] border-[1.5px] border-dashed border-[var(--ss-border)] text-xs font-semibold text-[var(--ss-text-muted)] hover:border-[var(--ss-brand)] hover:text-[var(--ss-brand)]">
          <PlusIcon className="h-3.5 w-3.5" aria-hidden="true" />{term.items.length ? 'Add' : 'Add a course or drop a slot here'}
        </button>
      </div>
    </section>
  );
}

function EarnedColumn({ relations }: { relations: ReturnType<typeof useRelations> }) {
  const { completed, programs, nameOf, creditsOf, earnedCredits, selectedCode, focusRequirementKey, requirementByKey, open, completedMode } = usePlanner();
  const mobile = useIsMobile();
  const [expanded, setExpanded] = useState(false);
  const counted = useMemo(() => Array.from(completed).filter((code) => requirementsForCourse(code, programs).length), [completed, programs]);
  const others = Array.from(completed).filter((code) => !counted.includes(code));
  // On phones, and when the student compacts finished work, the earned list stays folded so the plan itself comes first.
  const shown = expanded ? [...counted, ...others] : mobile || completedMode === 'compact' ? [] : counted;
  const hiddenCount = completed.size - shown.length;
  const focusPool = focusRequirementKey ? new Set(requirementByKey.get(focusRequirementKey)?.pool || []) : null;
  return (
    <section aria-label="Earned before this plan" data-board-column="earned" className="flex flex-col rounded-2xl border border-dashed border-[var(--ss-border)]">
      <header className="flex flex-col gap-1 border-b border-[var(--ss-border)] p-3">
        <div className="flex items-baseline justify-between"><h3 className="text-sm font-bold">Earned</h3><span className="text-xs tabular-nums text-[var(--ss-text-muted)]">{earnedCredits} cr</span></div>
        <span className="text-[11.5px] text-[var(--ss-text-muted)]">Before this plan · includes AP</span>
      </header>
      <div className="flex flex-col gap-2 p-2.5">
        {shown.map((code) => (
          <div key={code} data-board-card="">
            <CourseCard code={code} title={nameOf(code)} credits={creditsOf(code)} earned
              programIndexes={requirementsForCourse(code, programs).map((r) => r.programIndex)}
              selected={selectedCode === code}
              relation={relations.prereqs.has(code) ? 'prerequisite' : relations.unlocks.has(code) ? 'unlocks' : null}
              dimmed={Boolean(focusPool && !focusPool.has(code))}
              onOpen={(el) => open({ kind: 'course', code, anchor: el })} />
          </div>
        ))}
        {!completed.size ? <p className="p-1 text-xs text-[var(--ss-text-muted)]">No completed courses yet. Add them in Academic record.</p> : null}
        {hiddenCount > 0 || expanded ? (
          <button type="button" aria-expanded={expanded} onClick={() => setExpanded(!expanded)} className="min-h-12 px-1 text-left text-xs text-[var(--ss-text-muted)] hover:text-[var(--ss-text)] md:min-h-0">
            {expanded ? 'Show fewer' : shown.length ? `+ ${hiddenCount} other course${hiddenCount === 1 ? '' : 's'}` : `Show ${hiddenCount} earned course${hiddenCount === 1 ? '' : 's'}`}
          </button>
        ) : null}
      </div>
    </section>
  );
}

function GhostTermColumn({ ghost }: { ghost: ProposalTerm }) {
  return (
    <section aria-label={`${ghost.name}, suggested new term`} className="flex min-h-[220px] flex-col rounded-2xl border border-dashed border-[color-mix(in_srgb,var(--ss-brand)_55%,transparent)] md:min-h-[420px]">
      <header className="flex flex-col gap-1 border-b border-dashed border-[var(--ss-border)] p-3">
        <h3 className="text-sm font-bold text-[var(--ss-text-muted)]">{ghost.name}</h3>
        <span className="text-[11.5px] text-[var(--ss-text-muted)]">New term · not in your plan yet</span>
      </header>
      <div className="flex flex-col gap-2 p-2.5"><SuggestedBlock ghost={ghost} newTerm /></div>
    </section>
  );
}

export default function SemesterView() {
  const { terms, actions, programs, proposal, open: openOverlay, completedMode } = usePlanner();
  const relations = useRelations();
  const open = programs.reduce((sum, p) => sum + p.slotTotals.open, 0);
  const ghostByNumber = new Map(proposal.filter((g) => g.semesterNumber !== null).map((g) => [g.semesterNumber as number, g] as const));
  // Suggested terms that don't exist yet slot in chronologically between the real ones.
  const columns: Array<{ kind: 'term'; term: PlannerTerm } | { kind: 'ghost'; ghost: ProposalTerm }> = terms.map((term) => ({ kind: 'term' as const, term }));
  proposal.filter((g) => g.semesterNumber === null).forEach((ghost) => {
    const key = termOrderKey(ghost.name);
    const at = columns.findIndex((c) => termOrderKey(c.kind === 'term' ? c.term.name : c.ghost.name) > key);
    columns.splice(at < 0 ? columns.length : at, 0, { kind: 'ghost', ghost });
  });
  return (
    <div className="grid gap-4 lg:grid-cols-[300px_minmax(0,1fr)]">
      <RequirementRail className="hidden rounded-2xl border border-[var(--ss-border)] bg-[var(--ss-surface)] py-3 lg:flex lg:max-h-[calc(100dvh-220px)] lg:self-start lg:overflow-y-auto lg:sticky lg:top-24" />
      <div className="flex min-w-0 flex-col gap-3">
        {programs.length ? (
          <p className="rounded-xl bg-[var(--ss-surface-subtle)] px-3 py-2 text-xs text-[var(--ss-text-muted)] ring-1 ring-inset ring-[var(--ss-border)] lg:hidden">
            {open ? `${open} requirement slot${open === 1 ? '' : 's'} still open. See the Degree view to fill them.` : 'Every requirement slot is earned or planned.'}
          </p>
        ) : null}
        <h2 className="sr-only">Term board</h2>
        <div
          role="group"
          aria-label="Term board"
          data-release-scroll-reset
          aria-describedby="planner-board-keys"
          onKeyDown={(e) => handleBoardKeyDown(e, (semesterNumber, index, anchor) => openOverlay({ kind: 'move', semesterNumber, index, anchor }))}
          className="flex flex-col gap-3 md:grid md:auto-cols-[minmax(220px,1fr)] md:grid-flow-col md:items-start md:overflow-x-auto md:pb-2"
        >
          {completedMode === 'hide' ? null : <EarnedColumn relations={relations} />}
          {columns.map((column) => column.kind === 'term'
            ? <TermColumn key={column.term.number} term={column.term} relations={relations} ghost={ghostByNumber.get(column.term.number)} />
            : <GhostTermColumn key={`ghost-${column.ghost.name}`} ghost={column.ghost} />)}
          <button type="button" onClick={actions.addTerm} className="flex min-h-14 items-center justify-center gap-1.5 rounded-2xl border-[1.5px] border-dashed border-[var(--ss-border)] text-sm font-semibold text-[var(--ss-text-muted)] hover:border-[var(--ss-brand)] hover:text-[var(--ss-brand)] md:min-h-[420px]">
            <PlusIcon className="h-4 w-4" aria-hidden="true" />Add term
          </button>
        </div>
        <p id="planner-board-keys" className="hidden text-[11.5px] text-[var(--ss-text-muted)] md:block">
          Keyboard: arrow keys move between cards, <kbd className="rounded border border-[var(--ss-border)] px-1 font-mono text-[10.5px]">M</kbd> moves a course to another term, <kbd className="rounded border border-[var(--ss-border)] px-1 font-mono text-[10.5px]">Enter</kbd> opens details.
        </p>
      </div>
    </div>
  );
}
